/*
 * Copyright (c) 2025-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, ElementRef, OnInit, signal, ViewChild} from '@angular/core';
import {FormGroup, Validators} from '@angular/forms';
import {environment} from '../../../environments/environment';
import {CommonHelperService} from '../../service/common-helper.service';
import {EsciComponent} from "../esci.component";

interface Configuration {
    configKey: string;
    value: string;
    description: string;
    fileName: string;
    attachmentFile: File | null;
}

/** Index just past the JSON string literal that opens at {@code start}, escapes included. */
function stringEnd(text: string, start: number): number {
    let i = start + 1;
    while (i < text.length && text[i] !== '"') {
        i += text[i] === '\\' ? 2 : 1;
    }
    return i + 1;
}

function nextNonSpace(text: string, from: number): number {
    let i = from;
    while (i < text.length && /\s/.test(text[i])) {
        i++;
    }
    return i;
}

function indent(depth: number): string {
    return '\n' + '  '.repeat(depth);
}

/** Lays out the JSON token at {@code i} into {@code out}; returns the index just past it. */
function appendJsonToken(text: string, i: number, out: string[], layout: {depth: number}): number {
    const ch = text[i];
    if (ch === '"') {
        const end = stringEnd(text, i);
        out.push(text.slice(i, end));
        return end;
    }
    if (ch === '{' || ch === '[') {
        return appendOpening(text, i, out, layout);
    }
    if (ch === '}' || ch === ']') {
        layout.depth--;
        out.push(indent(layout.depth), ch);
    } else if (ch === ',') {
        out.push(',', indent(layout.depth));
    } else if (ch === ':') {
        out.push(': ');
    } else if (!/\s/.test(ch)) {
        out.push(ch);
    }
    return i + 1;
}

/** An empty object or array stays on one line; otherwise the bracket opens an indented block. */
function appendOpening(text: string, i: number, out: string[], layout: {depth: number}): number {
    const close = text[i] === '{' ? '}' : ']';
    const next = nextNonSpace(text, i + 1);
    if (text[next] === close) {
        out.push(text[i], close);
        return next + 1;
    }
    layout.depth++;
    out.push(text[i], indent(layout.depth));
    return i + 1;
}

@Component({
    selector: 'app-configuration',
    templateUrl: './configuration.component.html',
    styleUrls: ['./configuration.component.scss'],
    standalone: false
})
export class ConfigurationComponent extends EsciComponent implements OnInit {
    @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;
    private readonly configurationsSignal = signal<Configuration[]>([]);
    private readonly isLoadingSignal = signal(true);
    /** Keys whose JSON value is shown in full; every other JSON value shows as a one-line preview. */
    private readonly expandedJsonKeys = signal<ReadonlySet<string>>(new Set());
    /** Whether a value is a JSON object or array, cached by value so the template does not re-parse it. */
    private readonly jsonByValue = new Map<string, boolean>();
    baseFileURL = `${environment.apiUrl}/esciUI/filedata/`;
    editingIndex: number | null = null; // Tracks which row is being edited
    editValue = ''; // Stores the current value being edited
    originalConfig = null; // Stores the original value of the configuration before editing
    permissionUrl = '/esciUI/esciITAdminPermission';
    editForm: FormGroup;
    createForm: FormGroup;

    ngOnInit(): void {
        this.permission.set(false);
        this.esciService.checkPermission(this);
        this.fetchConfigurations();
        this.editForm = this.fb.group({
            name: [{value: '', disabled: true}],
            value: ['', Validators.required],
            description: [{value: '', disabled: true}],
        });

        this.createForm = this.fb.group({
            name: ['', Validators.required],
            value: ['', Validators.required],
            description: ['', Validators.required],
        });

    }

    get configurations(): Configuration[] {
        return this.configurationsSignal();
    }

    set configurations(value: Configuration[]) {
        this.configurationsSignal.set(value || []);
    }

    get isLoading(): boolean {
        return this.isLoadingSignal();
    }

    set isLoading(value: boolean) {
        this.isLoadingSignal.set(value);
    }

    fetchConfigurations() {
        this.isLoading = true;

        this.http.get<Configuration[]>(`${environment.apiUrl}/esciUI/configurations`).subscribe({
            next: (data) => {
                this.configurations = this.sortedForDisplay(data);
                this.isLoading = false;
            },
            error: (err) => {
                console.error('Error fetching configurations', err);
                this.isLoading = false;
            },
        });
    }

    startEdit(index: number, config: Configuration) {
        this.originalConfig = structuredClone(this.configurations[index]);
        this.editingIndex = index;
        // Pre-fill the input with the current value; a JSON document is laid out so it can be read.
        this.editValue = this.isJson(config) ? this.formatJson(config.value) : config.value;
    }

    /** Plain values first, then JSON documents (Auto Match rules, grid filters), each group by key. */
    private sortedForDisplay(configs: Configuration[]): Configuration[] {
        const jsonLast = (config: Configuration): number => (this.isJson(config) ? 1 : 0);
        const sorted = [...configs];
        sorted.sort((a, b) => jsonLast(a) - jsonLast(b) || (a.configKey || '').localeCompare(b.configKey || ''));
        return sorted;
    }

    /** A JSON object or array; a bare number, string or boolean stays a plain value. */
    isJson(config: Configuration): boolean {
        const value = config?.value ?? '';
        let json = this.jsonByValue.get(value);
        if (json === undefined) {
            try {
                const parsed = JSON.parse(value);
                json = parsed !== null && typeof parsed === 'object';
            } catch {
                json = false;
            }
            this.jsonByValue.set(value, json);
        }
        return json;
    }

    /**
     * Lays JSON text out with a two-space indent by moving whitespace only, never re-serialising a value:
     * JSON.parse would turn 9007199254740993 into 9007199254740992, and Save would then store the loss.
     */
    formatJson(value: string): string {
        const out: string[] = [];
        const layout = {depth: 0};
        let i = 0;
        while (i < value.length) {
            i = appendJsonToken(value, i, out, layout);
        }
        return out.join('');
    }

    isJsonExpanded(config: Configuration): boolean {
        return this.expandedJsonKeys().has(config.configKey);
    }

    toggleJson(config: Configuration): void {
        const next = new Set(this.expandedJsonKeys());
        if (!next.delete(config.configKey)) {
            next.add(config.configKey);
        }
        this.expandedJsonKeys.set(next);
    }

    saveEdit(config: Configuration) {
        if (this.editingIndex === null) {
            return;
        }
        const currentIndex = this.editingIndex;
        // Create a FormData object to hold the row and files
        const formData = new FormData();
        formData.append('name', config.configKey);
        formData.append('value', this.editValue);
        formData.append('description', config.description);
        formData.append('fileName', config.fileName);
        // Append files if they are provided
        if (config.attachmentFile) {
            formData.append('attachmentFile', config.attachmentFile);
        }

        this.http
            .post<boolean>(`${environment.apiUrl}/esciUI/configuration/${config.configKey}`, formData)
            .subscribe({
                next: (newConfig: any) => {
                    if (newConfig.fileName === 'null') {
                        newConfig.fileName = null;
                    }
                    const updatedConfigurations = [...this.configurations];
                    updatedConfigurations[currentIndex] = newConfig;
                    // A save can turn a value into JSON or out of it, so re-apply the display order.
                    this.configurations = this.sortedForDisplay(updatedConfigurations);
                    this.editingIndex = null; // Exit edit mode
                    this.editValue = '';
                    this.originalConfig = null;
                    alert('Configuration updated successfully.');
                },
                error: (err) => {
                    console.error('Error updating configuration', err);
                    alert('Failed to update configuration. ' + (err?.error?.message ?? ''));
                },
            });
    }

    triggerFileInput(index: number): void {
        this.originalConfig = structuredClone(this.configurations[index]);
        const fileInputElement = document.getElementById(`fileInput-${index}`) as HTMLInputElement;
        if (fileInputElement) {
            fileInputElement.click();
        }
    }

    onConfigFileSelected(index: number, config: Configuration, event: Event): void {
        const input = event.target as HTMLInputElement;
        config.attachmentFile = input.files?.[0];
        config.value = config.attachmentFile?.name || '';
        // Hide the current filename, because this is what the front end uses to know that the file can be accessed from
        // the server through the baseFileURL. Since this file is now local, we can't provide a URL to it.
        config.fileName = '';
    }

    cancelEdit(config: Configuration) {
        this.editingIndex = null; // Exit edit mode without saving
        this.editValue = '';
        // Restore the values before the edit started:
        if (this.originalConfig) {
            config.value = this.originalConfig.value;
            config.fileName = this.originalConfig.fileName;
            this.originalConfig = null;
        }
    }

    getFile(fileId: string, fileName: string) {
        this.downloadService.exportConfigFile(this.baseFileURL + fileId).subscribe(blob => {
            CommonHelperService.downloadFile(blob, fileName);
        });
    }
}
